import React from 'react';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FiRefreshCw } from '@react-icons/all-files/fi/FiRefreshCw';
import { FiPlus } from '@react-icons/all-files/fi/FiPlus';
import { FiTrash2 } from '@react-icons/all-files/fi/FiTrash2';
import { FaChevronDown } from '@react-icons/all-files/fa/FaChevronDown';
import { FaChevronUp } from '@react-icons/all-files/fa/FaChevronUp';
import { Select, Button } from 'antd';
import { ALL_LANGUAGES } from './constants';
import './index.scss';

const PROFICIENCY_LEVELS = [
  { value: '', label: 'Not specified' },
  { value: 'Basic', label: 'Basic' },
  { value: 'Conversational', label: 'Conversational' },
  { value: 'Fluent', label: 'Fluent' },
  { value: 'Native', label: 'Native' },
  { value: 'Professional', label: 'Professional' },
];

function getRandomLanguages(exclude = [], count = 7) {
  const available = ALL_LANGUAGES.filter(l => !exclude.includes(l));
  const shuffled = available.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

const initialSuggested = [
  'English',
  'Spanish',
  'French',
  'German',
  'Chinese',
  'Japanese',
  'Portuguese',
  'Indian',
];

const LanguageStep = (props) => {
  const { params, onChange } = props;
  const languages = params.languages || [];
  const [suggestedLanguages, setSuggestedLanguages] = React.useState(initialSuggested);
  const [openLanguages, setOpenLanguages] = React.useState(new Set());

  // Open the first item by default if it exists
  React.useEffect(() => {
    if (languages.length > 0 && openLanguages.size === 0) {
      setOpenLanguages(new Set([0]));
    }
  }, [languages.length, openLanguages.size]);

  const onContinueClick = () => {
    props.onContinueClick && props.onContinueClick();
  };

  const onBackClick = () => {
    props.onBackClick && props.onBackClick();
  };

  const addLanguage = (name = '') => {
    const newLanguages = [...languages, { name, proficiency: '' }];
    onChange('languages', newLanguages);
    // Open the newly added item
    const newIndex = newLanguages.length - 1;
    setOpenLanguages(prev => new Set([...prev, newIndex]));
  };

  const removeLanguage = (idx) => {
    const newLanguages = languages.filter((_, i) => i !== idx);
    onChange('languages', newLanguages);
    // Remove from openLanguages if it was open
    setOpenLanguages(prev => {
      const newOpen = new Set(prev);
      newOpen.delete(idx);
      // Adjust indices for items after the removed one
      const adjustedOpen = new Set();
      newOpen.forEach(openIdx => {
        if (openIdx < idx) {
          adjustedOpen.add(openIdx);
        } else {
          adjustedOpen.add(openIdx - 1);
        }
      });
      return adjustedOpen;
    });
  };

  const updateLanguage = (idx, field, value) => {
    const newLanguages = languages.map((l, i) => i === idx ? { ...l, [field]: value } : l);
    onChange('languages', newLanguages);
  };

  const handleSuggestedClick = (language) => {
    setSuggestedLanguages(suggestedLanguages.filter(l => l !== language));
    addLanguage(language);
  };

  const toggleLanguageCollapse = (idx) => {
    setOpenLanguages(prev => {
      const newOpen = new Set(prev);
      if (newOpen.has(idx)) {
        newOpen.delete(idx);
      } else {
        newOpen.add(idx);
      }
      return newOpen;
    });
  };

  return (
    <div className="step languages-step">
      <div className="step-inner-content">
        <div className="step-header">
          <h2>Languages</h2>
          <p>Highlight your language skills and proficiency levels.</p>
        </div>
        <div className="languages-bar">
          <span className="suggested-chip">✧ Suggested</span>
          {suggestedLanguages.map((language, i) => (
            <span className="language-chip" key={language} onClick={() => handleSuggestedClick(language)}>{language}</span>
          ))}
        </div>
        <div className="languages-list">
          {languages.map((language, idx) => (
            <div className="language-item" key={idx}>
              <div className="left">
                <div className="language-header">
                  <div className="language-main" onClick={() => toggleLanguageCollapse(idx)}>
                    <div className="language-name">
                      {language.name || 'Untitled'}
                      {language.proficiency && <span className="language-proficiency-indicator"> • {language.proficiency}</span>}
                    </div>
                    <div className="chevron-icon">
                      {!openLanguages.has(idx) ? <FaChevronDown size={16} /> : <FaChevronUp size={16} />}
                    </div>
                  </div>
                </div>
                {openLanguages.has(idx) && (
                  <div className="language-details">
                    <div className="field">
                      <label>Language</label>
                      <input
                        type="text"
                        placeholder="Language name"
                        value={language.name}
                        onChange={e => updateLanguage(idx, 'name', e.target.value)}
                        list="all-languages-list"
                      />
                      <datalist id="all-languages-list">
                        {ALL_LANGUAGES.map(l => <option key={l} value={l} />)}
                      </datalist>
                    </div>
                    <div className="field">
                      <label>Proficiency</label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select proficiency level"
                        value={language.proficiency}
                        onChange={val => updateLanguage(idx, 'proficiency', val)}
                        options={PROFICIENCY_LEVELS}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="right">
                <div className="delete-btn" onClick={(e) => { e.stopPropagation(); removeLanguage(idx); }} title="Remove language">
                  <FiTrash2 size={19} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="add-object-row">
          <button className="add-object-btn" onClick={() => addLanguage()}>
            <FiPlus size={22} /> Add one more language
          </button>
        </div>
      </div>
      <div className="step-footer">
        <button className="back-btn" onClick={onBackClick}>&larr; Back</button>
        <Button loading={props.formLoading} className="next-btn" onClick={onContinueClick}>Next <span className="arrow">&rarr;</span></Button>
      </div>
    </div>
  );
};

export default LanguageStep;
